<script setup lang="ts">
import BaseModal from '@/components/modal/BaseModal.vue';
import { COLOR_CLASSES } from '@/constants/colors';
import { ref } from 'vue';
import ModalFooterConfirmButtons from '@/components/modal/ModalFooterConfirmButtons.vue';
import { DharmaEditModalProps, useModalStore } from '@/stores/modal';
import InputRow from '@/components/TextField/inputRow.vue';
import CalendarComponent from '@/components/calendar/CalendarComponent.vue';
import { Dayjs } from 'dayjs';

interface Emits {
  (e: 'confirm'): void;
}

const props = defineProps<DharmaEditModalProps>();
const emit = defineEmits<Emits>();

const modalStore = useModalStore();

const beophoeItem = props.beophoeItem;
const isSubmitting = ref(false);
const dharmaDate = ref(beophoeItem?.date);
const dharmaLink = ref(beophoeItem?.link_url);
const isCalendarComponentOpen = ref(false);
const selectedCalendarDate = ref<Date | null>(null)
// console.log('dharmaDate', dharmaDate.value);
// console.log('dharmaLink', dharmaLink.value);

const handleClose = () => {
  modalStore.close();
};

const handleSubmit = () => {
  if (!beophoeItem?.id || !dharmaDate.value || !dharmaLink.value) return;

  // 날짜 형식 검증 (yyyy.mm.dd)
  const dateRegex = /^\d{4}\.\d{2}\.\d{2}$/;
  if (!dateRegex.test(dharmaDate.value)) {
    alert('날짜 형식이 올바르지 않습니다. (예: 2024.03.15)');
    return;
  }

  isSubmitting.value = true;

  // onSubmit 실행 후 로딩 상태 해제
  props.onSubmit(beophoeItem.id, dharmaDate.value, dharmaLink.value, props.memId, beophoeItem.title);

  // 약간의 지연 후 모달 닫기 (로딩 상태가 보이도록)
  setTimeout(() => {
    isSubmitting.value = false;
    handleClose();
  }, 300);
};

const handleCalendarComponentDateSelection = (date: Date) => {
  // 날짜 유효성 검사
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    console.error('유효하지 않은 날짜:', date);
    return;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  // 각 값이 유효한지 확인
  if (isNaN(year) || isNaN(parseInt(month)) || isNaN(parseInt(day))) {
    console.error('날짜 파싱 오류:', { year, month, day });
    return;
  }

  dharmaDate.value = `${year}.${month}.${day}`;
  isCalendarComponentOpen.value = false;
};
</script>

<template>
  <BaseModal title="법문 수정하기" width="360px" wrapper-class="gap-3" @close="handleClose">
    <template #overlay>
      <CalendarComponent
        v-if="isCalendarComponentOpen"
        v-model="selectedCalendarDate"
        v-model:isVisible="isCalendarComponentOpen"
        :disable-past-dates="true"
        class="max-w-[330px]"
        @selectDate="handleCalendarComponentDateSelection"
      />
    </template>

    <div class="flex flex-col gap-4 my-6">
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-gray-700">법회 일자</label>
        <div class="flex flex-row items-center gap-2">
          <InputRow v-model="dharmaDate" label="" placeholder="YYYY.MM.DD" readonly />
          <button @click="isCalendarComponentOpen = !isCalendarComponentOpen" type="button" class="px-1 py-1">
            <VIcon icon="ri-calendar-event-line" size="24" color="#757575" />
          </button>
        </div>
      </div>
      <InputRow :max-length="100" v-model="dharmaLink" label="법회 링크" />
    </div>

    <template #footer>
      <ModalFooterConfirmButtons
        cancel-text="취소"
        confirm-text="등록하기"
        :is-loading="isSubmitting"
        :disabled="
          !dharmaDate || !dharmaLink || (dharmaDate === beophoeItem?.date && dharmaLink === beophoeItem?.link_url)
        "
        @cancel="handleClose"
        @confirm="handleSubmit"
      />
    </template>
  </BaseModal>
</template>
